import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { FiCheck, FiChevronDown, FiChevronUp, FiMessageCircle, FiSend } from 'react-icons/fi';

// Panels on the Feedback screen that sit around the response list:
//   ToSendPanel   — surveys that are due and not yet sent (everyone sees their own)
//   InsightsPanel — what the answers say added together (Chairman, CEO, COO)
//   FollowUpBox   — close an open follow-up with a note (Chairman, CEO, COO)

const KIND_LABEL = { STUDENT: 'Student', JOB: 'Job seeker', TOURIST: 'Tourist', MEDICAL: 'Medical' };

function ToSendRow({ row, showConsultant, onSent }) {
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/feedback/', {
        client_kind: row.client_kind, client_id: row.client_id, moment: row.moment,
        respondent_role: row.respondent_role, respondent_name: row.respondent_name, respondent_phone: row.respondent_phone,
      });
      setCreated(data);
    } catch (e) {
      const d = e?.response?.data || {};
      setError(d.detail || Object.values(d).flat()[0] || 'Could not create the link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`fb-send-row ${row.overdue ? 'overdue' : ''}`}>
      <div className="fb-send-who">
        <strong>{row.respondent_role === 'SPONSOR' ? row.respondent_name : row.client_name}</strong>
        <span className={`fb-chip kind-${row.client_kind}`}>{KIND_LABEL[row.client_kind]}</span>
        <div className="fb-muted">
          {row.moment_label} · {row.client_ref}
          {' · '}{row.days_waiting === 0 ? 'due today' : `waiting ${row.days_waiting} day${row.days_waiting === 1 ? '' : 's'}`}
          {showConsultant && ` · ${row.consultant_name || 'no consultant'}`}
        </div>
        {error && <div className="fb-error">{error}</div>}
      </div>
      {created ? (
        <div className="fb-send-actions">
          {created.whatsapp_url ? (
            <a className="fb-btn fb-btn-wa" href={created.whatsapp_url} target="_blank" rel="noreferrer" onClick={onSent}>
              <FiMessageCircle /> Send on WhatsApp
            </a>
          ) : (
            <span className="fb-muted">No phone on file. Copy it from "Awaiting reply".</span>
          )}
          <button type="button" className="fb-btn" onClick={onSent}>Done</button>
        </div>
      ) : (
        <button type="button" className="fb-btn fb-btn-primary" disabled={busy} onClick={create}>
          <FiSend /> {busy ? 'Creating…' : 'Create link'}
        </button>
      )}
    </li>
  );
}

function ToSendList({ title, hint, rows, showConsultant, onSent }) {
  if (!rows.length) return null;
  return (
    <div className="fb-send-group">
      <h3>{title} <span className="fb-count">{rows.length}</span></h3>
      {hint && <p className="fb-muted">{hint}</p>}
      <ul>
        {rows.map((row) => (
          <ToSendRow key={`${row.client_kind}${row.client_id}${row.moment}${row.respondent_role}`}
            row={row} showConsultant={showConsultant} onSent={onSent} />
        ))}
      </ul>
    </div>
  );
}

export function ToSendPanel({ onSent }) {
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/feedback/to-send/').then(({ data: d }) => {
      setData(d);
      setStartDate(d.start_date || '');
    }).catch(() => setData(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const sent = () => { load(); onSent(); };
  const saveStart = async () => {
    setSaving(true);
    try {
      await api.patch('/settings/', { feedback_prompt_start_date: startDate });
      load();
    } finally {
      setSaving(false);
    }
  };
  const total = data.mine.length + data.overdue.length + data.unassigned.length;

  return (
    <section className="fb-panel">
      <div className="fb-panel-head">
        <h2>Surveys to send {total > 0 && <span className="fb-count">{total}</span>}</h2>
        {data.leadership && (
          <label className="fb-start">
            Prompt for clients who reach a survey point from
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <button type="button" className="fb-btn" disabled={saving || startDate === (data.start_date || '')} onClick={saveStart}>
              Save
            </button>
          </label>
        )}
      </div>
      {!data.start_date && (
        <p className="fb-muted">
          Survey prompts are switched off. {data.leadership
            ? 'Choose a start date above to switch them on.'
            : 'Management switches them on by choosing a start date.'}
        </p>
      )}
      {data.start_date && total === 0 && <p className="fb-muted">Nothing to send right now.</p>}
      <ToSendList title="Your clients" rows={data.mine} onSent={sent} />
      <ToSendList title="Overdue" hint="Due more than 7 days ago and still not sent by the consultant."
        rows={data.overdue} showConsultant onSent={sent} />
      <ToSendList title="No consultant" hint="The client has no internal consultant, so these fall to management."
        rows={data.unassigned} showConsultant onSent={sent} />
    </section>
  );
}

const pct = (n) => (n == null ? '—' : `${n}%`);

export function InsightsPanel({ clientKind }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const params = {};
    if (clientKind) params.client_kind = clientKind;
    if (from) params.from = from;
    if (to) params.to = to;
    setError('');
    api.get('/feedback/insights/', { params }).then(({ data: d }) => setData(d))
      .catch(() => setError('Could not load the summary.'));
  }, [open, clientKind, from, to]);

  return (
    <section className="fb-panel">
      <div className="fb-panel-head">
        <button type="button" className="fb-panel-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? <FiChevronUp /> : <FiChevronDown />} <h2>What the answers say</h2>
        </button>
        {open && (
          <div className="fb-range">
            <label>Links sent from <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label>to <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          </div>
        )}
      </div>
      {open && error && <div className="fb-error">{error}</div>}
      {open && data && (
        data.responses === 0 ? <p className="fb-muted">No answered surveys in this range yet.</p> : (
          <div className="fb-insights">
            <div className="fb-insight">
              <h3>Weakest stages first</h3>
              {data.stages.length === 0 ? <p className="fb-muted">No stage ratings yet.</p> : (
                <table>
                  <thead><tr><th>Stage</th><th>Client type</th><th>Average</th><th>Answers</th></tr></thead>
                  <tbody>
                    {data.stages.map((s) => (
                      <tr key={s.client_kind + s.stage} className={s.average < 3 ? 'low' : ''}>
                        <td>{s.stage}</td><td>{s.client_kind_label}</td><td>{s.average}/5</td><td>{s.answers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="fb-insight">
              <h3>Did it match what we promised?</h3>
              {data.promise_match.length === 0 ? <p className="fb-muted">No answers yet.</p> : (
                <table>
                  <thead><tr><th>Client type</th><th>Yes</th><th>Partly</th><th>No</th><th>Yes rate</th></tr></thead>
                  <tbody>
                    {data.promise_match.map((p) => (
                      <tr key={p.client_kind}>
                        <td>{p.client_kind_label}</td><td>{p.YES}</td><td>{p.PARTLY}</td><td>{p.NO}</td><td>{pct(p.yes_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="fb-insight">
              <h3>Unexpected costs: {pct(data.unexpected_cost.yes_pct)} of {data.unexpected_cost.answers} answer{data.unexpected_cost.answers === 1 ? '' : 's'}</h3>
              {data.unexpected_cost.named.length === 0 ? <p className="fb-muted">None reported.</p> : (
                <ul className="fb-plain">
                  {data.unexpected_cost.named.map((n) => (
                    <li key={n.id}><strong>{n.client_name}</strong> ({n.client_kind_label}): {n.what || 'no detail given'}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="fb-insight">
              <h3>Recommend score (NPS)</h3>
              <table>
                <thead><tr><th>Group</th><th>Score</th><th>Answers</th></tr></thead>
                <tbody>
                  {data.recommend.map((r) => (
                    <tr key={r.label}><td>{r.label}</td><td>{r.score ?? '—'}</td><td>{r.answers}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="fb-insight">
              <h3>Check-in versus end of journey</h3>
              {data.journey.clients === 0 ? (
                <p className="fb-muted">No client has answered both surveys yet.</p>
              ) : (
                <p>
                  Satisfaction went from <strong>{data.journey.check_in}/5</strong> at check-in to{' '}
                  <strong>{data.journey.end_review}/5</strong> at the end, across {data.journey.clients} client
                  {data.journey.clients === 1 ? '' : 's'} who answered both.
                </p>
              )}
            </div>

            <div className="fb-insight">
              <h3>Links sent and answered</h3>
              <table>
                <thead><tr><th>Sent by</th><th>Sent</th><th>Answered</th><th>Rate</th></tr></thead>
                <tbody>
                  {data.response_rate.map((r) => (
                    <tr key={r.consultant}><td>{r.consultant}</td><td>{r.sent}</td><td>{r.answered}</td><td>{pct(r.answered_pct)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="fb-insight">
              <h3>Follow-ups past their deadline: {data.overdue_followups.length}</h3>
              {data.overdue_followups.length === 0 ? <p className="fb-muted">None. Every follow-up is on time.</p> : (
                <ul className="fb-plain">
                  {data.overdue_followups.map((o) => (
                    <li key={o.id}>
                      <strong>{o.client_name}</strong> ({o.client_kind_label}), due {new Date(o.due_at).toLocaleDateString()}
                      {o.owner ? `, owner ${o.owner}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )
      )}
    </section>
  );
}

export function FollowUpBox({ item, onClosed }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (item.followup_status === 'CLOSED') {
    return (
      <div className="fb-followup closed">
        <strong><FiCheck /> Follow-up closed by {item.closed_by_name}</strong>
        <p>{item.closure_note}</p>
      </div>
    );
  }
  if (item.followup_status !== 'OPEN') return null;

  const close = async () => {
    if (!note.trim()) { setError('Write what was done before closing.'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(`/feedback/${item.id}/close_followup/`, { note: note.trim() });
      onClosed(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not close the follow-up.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fb-followup">
      <strong>Follow-up open</strong>
      <span className="fb-muted">
        {' '}Owner {item.followup_owner_name || 'not set'} · due {new Date(item.followup_due_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
      </span>
      <label className="fb-label">
        What was done?
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called her, refund agreed" />
      </label>
      {error && <div className="fb-error">{error}</div>}
      <button type="button" className="fb-btn fb-btn-primary" disabled={busy} onClick={close}>
        <FiCheck /> Close follow-up
      </button>
    </div>
  );
}
